import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, setPageMeta } from "@/lib/api";
import { MaskedLines, Reveal } from "@/components/Motion";
import { JoinForm } from "@/components/JoinForm";

export default function JoinPage() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    setPageMeta(
      "Join Ninja Run Club: Weekly Runs & Training Emails",
      "Join Ninja Run Club free: one useful email a week with the next run near you, evidence-backed training, free tools, and first access to drops. No tryouts, no minimum pace."
    );
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="light-section bg-paper text-ink min-h-screen">
      <div className="px-5 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-2 gap-16">
        <div>
          <h1 className="font-mono-label text-ink/40">JOIN NINJA RUN CLUB · NO TRYOUTS. NO MINIMUM PACE.</h1>
          <MaskedLines
            lines={["SHOW UP.", "PUT IN THE MILES.", "BRING OTHERS", "WITH YOU."]}
            lineClassName="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95]"
            className="mt-6"
          />
          <p className="text-ink/60 text-sm max-w-md mt-8">
            One useful email a week: the next run near you, training that works, tools, and first access to drops. Unsubscribe whenever. We would rather earn the open.
          </p>
          {stats && (
            <p data-testid="join-proof-bar" className="font-mono-label text-ink/50 mt-6 border-t border-ink/15 pt-5">
              {stats.clubs} CLUBS · {stats.cities} CITIES · {stats.articles} FREE GUIDES · HUMAN-VERIFIED
            </p>
          )}
        </div>
        <Reveal delay={0.2}>
          <JoinForm source="join-page" />
        </Reveal>
      </div>

      <section className="px-5 lg:px-10 pb-16 grid grid-cols-1 lg:grid-cols-2 gap-16 border-t border-ink/10 pt-14" data-testid="join-what-you-get">
        <div>
          <h2 className="font-display text-2xl lg:text-3xl">WHAT YOU GET.</h2>
          <ul className="mt-6 space-y-4">
            {[
              ["THE NEXT RUN NEAR YOU", "Weekly meetups pulled from a human-verified directory of run clubs across America — filtered to your city when you tell us where you run."],
              ["TRAINING THAT WORKS", "Evidence-backed guides and plans for every distance from a first 5K to marathons, ultras, and HYROX. A direct answer up top, the depth to act on it below."],
              ["FREE TOOLS, FIRST ACCESS", "Twenty-one calculators — pace, race prediction, fueling, heart-rate zones — plus first word on limited City Series drops before they go live."],
            ].map(([title, desc]) => (
              <li key={title} className="border-l-2 border-ink pl-5">
                <p className="font-mono-label text-ink/70">{title}</p>
                <p className="text-ink/60 text-sm mt-1.5">{desc}</p>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h2 className="font-display text-2xl lg:text-3xl">HOW IT WORKS.</h2>
          <ol className="mt-6 space-y-4">
            {[
              ["Drop your email and city.", "That is the whole signup. Pace and goal are optional — they just sharpen what we send you."],
              ["Get one email a week.", "The next run near you, one piece of training worth reading, and a tool worth using. No daily drip, no fake urgency."],
              ["Show up.", "Use the directory to pick a club, run the first-visit checklist, and go. Every pace has a place — including walkers."],
            ].map(([title, desc], i) => (
              <li key={title} className="flex gap-4">
                <span className="font-mono-label text-ink/30 mt-0.5">0{i + 1}</span>
                <span>
                  <p className="text-ink/90 text-sm font-semibold">{title}</p>
                  <p className="text-ink/60 text-sm mt-1">{desc}</p>
                </span>
              </li>
            ))}
          </ol>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/clubs" data-testid="join-browse-clubs-link" className="font-mono-label border border-ink/30 px-4 py-2.5 hover:bg-ink hover:text-paper transition-colors duration-200">
              BROWSE THE DIRECTORY →
            </Link>
            <Link to="/ranked" className="font-mono-label border border-ink/30 px-4 py-2.5 hover:bg-ink hover:text-paper transition-colors duration-200">
              THE 2026 RANKINGS →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
